import React, { useEffect, useState, useRef } from 'react';
import Footer from './components/Footer';
import Header from './components/Header';
import SearchBox from './components/SearchBox';
import SectionCards from './components/SectionCards';
import Carousel from './components/Carousel';
import Alert from './components/Alert';
import Summary from './components/Summary';
import Dropdown from './components/Dropdown';
import MaintenancePage from './components/MaintenancePage';
import { useDebounce } from 'use-debounce';
import funFictionalCourses from './data/funFictionalCourses'; // Importing fictional courses data for testing

function App() {

  const [matrixMode, setMatrixMode] = useState(false);
  const matrixIntervalRef = useRef(null);

  // Easter Egg state
  const [easterEggActive, setEasterEggActive] = useState(false);
  const [easterEggMessage, setEasterEggMessage] = useState(null);

  // Helper function to clear all filters
  const clearAllFilters = () => {
    setSelectedSubjectCode([]);
    setSelectedLocation([]);
    setSelectedPtrm([]);
    setSelectedInstructor([]);
    setSelectedAttribute([]);
    setSelectedDaysOfWeek([]);
    setSelectedStartTime('');
    setSelectedEndTime('');
    setSelectedScheduleCode([]);
    setCourseNumberSearch('');
    setFilterZTCOnly(false);
    setFilterOpenClasses(false);
    setFilterWaitlistClasses(false);
    setFilterCredit(false);
    setFilterNonCredit(false);
  };

  const apiUrl = process.env.REACT_APP_API_URL;

  const [selectedSubjectCode, setSelectedSubjectCode] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedPtrm, setSelectedPtrm] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedAttribute, setSelectedAttribute] = useState([]);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [sectionData, setSectionsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchBoxText, setSearchBoxText] = useState('');
  const [debouncedSearch] = useDebounce(searchBoxText, 300);
  const [filterZTCOnly, setFilterZTCOnly] = useState(false);
  const [filterOpenClasses, setFilterOpenClasses] = useState(false);
  const [filterWaitlistClasses, setFilterWaitlistClasses] = useState(false);
  const [filterCredit, setFilterCredit] = useState(false);
  const [filterNonCredit, setFilterNonCredit] = useState(false);
  const [ufoActive, setUfoActive] = useState(false);
  const [ufoTop, setUfoTop] = useState('50vh');
  const [ufoDirection, setUfoDirection] = useState('ltr'); // 'ltr' (left→right) or 'rtl' (right→left)

  // State variables for filter dropdowns
  const [subjectCodeOptions, setSubjectCodeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [ptrmOptions, setPtrmOptions] = useState([]);
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [attributeOptions, setAttributeOptions] = useState([]);
  // --- SSRMEET Schedule Code filter state ---
  const [selectedScheduleCode, setSelectedScheduleCode] = useState([]);
  const [scheduleCodeOptions, setScheduleCodeOptions] = useState([]);
  // --- Course Number Search state ---
  const [courseNumberSearch, setCourseNumberSearch] = useState('');


  const fetchWithRetry = async (url, options = {}, retries = 5, delay = 1500) => {
    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(url, options);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return await res.json();
      } catch (error) {
        if (i < retries - 1) await new Promise(res => setTimeout(res, delay));
        else throw error;
      }
    }
  };

  useEffect(() => {
    if (selectedTerm) {
      setLoading(true);
      setSectionsData([]);
      setFilteredData([]);


      fetchWithRetry(`${apiUrl}/classSchedule/${selectedTerm}?limit=1000&pages=6`)

        .then((data) => {

          setSectionsData(data);
          setLoading(false);
        })
        .catch(error => {
          console.error("Failed to fetch section data after retries:", error);
          setSectionsData([]);
          setLoading(false);
        });
    } else {
      setSectionsData([]);
      setFilteredData([]);
      // Clear dependent dropdown options
      setSubjectCodeOptions([]);
      setLocationOptions([]);
      setPtrmOptions([]);
      setInstructorOptions([]);
      setAttributeOptions([]);
      setLoading(false);

      clearAllFilters();
    }
  }, [selectedTerm]);

  // Populate dropdown options from sectionData
  useEffect(() => {
    // Ensure sectionData is not null or undefined before mapping
    if (sectionData && sectionData.length > 0) {
      const subjectCodes = [...new Set(sectionData.map(item => `${item.subjCode || ''} - ${item.subjDesc || ''}`.trim()).filter(Boolean))].sort();
      const locations = [...new Set(sectionData.map(item => item.campDesc).filter(Boolean))].sort();
      const ptrms = [...new Set(sectionData.map(item => item.ptrmDesc).filter(Boolean))].sort();
      const instructors = [...new Set(sectionData.map(item => `${item.pfaName}`).filter(Boolean))].sort();
      const attributes = [...new Set(sectionData.map(item => item.saprDesc).filter(Boolean))].sort();
      // SSRMEET Schedule Code options
      const scheduleCodes = [...new Set(sectionData.map(item => item.schdDesc).filter(Boolean))].sort();

      setSubjectCodeOptions(subjectCodes);
      setLocationOptions(locations);
      setPtrmOptions(ptrms);
      setInstructorOptions(instructors);
      setAttributeOptions(attributes);
      setScheduleCodeOptions(scheduleCodes);
    } else {
      // If sectionData is empty, clear the options
      setSubjectCodeOptions([]);
      setLocationOptions([]);
      setPtrmOptions([]);
      setInstructorOptions([]);
      setAttributeOptions([]);
      setScheduleCodeOptions([]);
    }
  }, [sectionData]);



  const shouldShowFunCourses = () =>
    selectedInstructor.includes("Staff") &&
    selectedLocation.includes("Off-campus") &&
    selectedDaysOfWeek.includes("U") &&
    selectedSubjectCode.length === 0 &&
    courseNumberSearch.trim() === '' &&
    selectedPtrm.length === 0 &&
    selectedAttribute.length === 0 &&
    selectedScheduleCode.length === 0 &&
    !selectedStartTime &&
    !selectedEndTime &&
    !filterZTCOnly &&
    !filterOpenClasses &&
    !filterWaitlistClasses &&
    !filterCredit &&
    !filterNonCredit &&
    (!debouncedSearch || debouncedSearch.trim() === "");

  useEffect(() => {
    let filtered = sectionData;

    if (shouldShowFunCourses()) {
      setFilteredData(funFictionalCourses);
      return;
    }

    // Subject Code filter
    if (selectedSubjectCode && selectedSubjectCode.length > 0) {
      filtered = filtered.filter(item => {
        const formattedCode = `${item.subjCode || ''} - ${item.subjDesc || ''}`.trim();
        return selectedSubjectCode.includes(formattedCode);
      });
    }

    // Course Number search filter
    if (courseNumberSearch.trim() !== '') {
      const query = courseNumberSearch.toLowerCase();
      filtered = filtered.filter(item => item.courseNumbDisplay?.toLowerCase().includes(query));
    }

    // Location filter
    if (selectedLocation && selectedLocation.length > 0) {
      filtered = filtered.filter(item => item.campDesc && selectedLocation.includes(item.campDesc));
    }

    // schedule type filter
    if (selectedPtrm && selectedPtrm.length > 0) {
      filtered = filtered.filter(item => item.ptrmDesc && selectedPtrm.includes(item.ptrmDesc));
    }

    // Instructor filter
    if (selectedInstructor && selectedInstructor.length > 0) {
      filtered = filtered.filter(item => {
        const instructorName = `${item.pfaName}`;
        return instructorName && selectedInstructor.includes(instructorName);
      });
    }

    // Attribute filter
    if (selectedAttribute && selectedAttribute.length > 0) {
      filtered = filtered.filter(item => {
        const attributes = item.attrchrts ? item.attrchrts.split(',').map(a => a.trim()) : [];
        return selectedAttribute.every(attr => attributes.includes(attr));
      });
    }

    // SSRMEET Schedule Code filter
    if (selectedScheduleCode && selectedScheduleCode.length > 0) {
      filtered = filtered.filter(item => selectedScheduleCode.includes(item.schdDesc));
    }

    // Days of Week filter
    if (selectedDaysOfWeek && selectedDaysOfWeek.length > 0) {
      filtered = filtered.filter(item => {
        const days = [item.monDay, item.tueDay, item.wedDay, item.thuDay, item.friDay, item.satDay, item.sunDay]
          .filter(Boolean) // Remove null/undefined days
          .map(d => d.trim().toUpperCase());
        return days.length > 0 && selectedDaysOfWeek.some(day => days.includes(day));
      });
    }

    // Start and End Time filter
    if ((selectedStartTime && selectedStartTime !== '') || (selectedEndTime && selectedEndTime !== '')) {
      filtered = filtered.filter(item => {
        const classStartValid = item.beginTime && /^\d{4}$/.test(item.beginTime);
        const classEndValid = item.endTime && /^\d{4}$/.test(item.endTime);

        const classStart = classStartValid ? parseInt(item.beginTime, 10) : null;
        const classEnd = classEndValid ? parseInt(item.endTime, 10) : null;

        const startFilter = selectedStartTime && selectedStartTime !== '' ? parseInt(selectedStartTime.replace(':', ''), 10) : null;
        const endFilter = selectedEndTime && selectedEndTime !== '' ? parseInt(selectedEndTime.replace(':', ''), 10) : null;

        let startTimeMatches = true;
        if (startFilter !== null) {
          startTimeMatches = classStart !== null && classStart >= startFilter;
        }

        let endTimeMatches = true;
        if (endFilter !== null) {
          endTimeMatches = classEnd !== null && classEnd <= endFilter;
        }

        return startTimeMatches && endTimeMatches;
      });
    }

    // Zero Textbook Costs filter
    if (filterZTCOnly) {
      filtered = filtered.filter(item =>
        item.attrchrts && item.attrchrts.split(',').map(a => a.trim()).includes('ZTC')
      );
    }

    // Open Classes and Waitlist Classes filters
    if (filterOpenClasses || filterWaitlistClasses) {
      filtered = filtered.filter(item => {
        const status = item.fwSectOpenInd;
        return (filterOpenClasses && status === 'O') || (filterWaitlistClasses && status === 'W');
      });
    }

    // Credit and Non-Credit filters
    if (filterCredit) {
      filtered = filtered.filter(item => parseFloat(item.creditHrLow) > 0);
    }
    if (filterNonCredit) {
      filtered = filtered.filter(item => parseFloat(item.creditHrLow) === 0);
    }

    // Search Box filter
    if (debouncedSearch && debouncedSearch.trim() !== "") {
      const query = debouncedSearch.toLowerCase();
      const terms = query.split(/\s+/).filter(Boolean);

      filtered = filtered.filter(item => {
        const valuesToMatch = [
          item.courseTitle?.toLowerCase(),
          item.subjCode?.toLowerCase(),
          item.subjDesc?.toLowerCase(),
          item.courseNumbDisplay?.toLowerCase(),
          item.crn && String(item.crn)
        ].filter(Boolean).join(' ');

        return terms.every(term => valuesToMatch.includes(term));
      });
    }

    // Excessive filter selection
    const excessiveFilters = [
      selectedSubjectCode.length > 20,
      selectedInstructor.length > 10,
      selectedLocation.length > 5,
      selectedDaysOfWeek.length === 7
    ];

    const tooManyFilters = excessiveFilters.filter(Boolean).length >= 2;
    if (tooManyFilters && filtered.length === 0) {
      if (!easterEggActive) {
        const easterEggMessages = [
          {
            courseTitle: "Whoa there, ambitious! 😵",
            sectionNote: "Trying to take every class at once? That's some superhero-level energy! 🦸‍♀️🦸‍♂️<br /><br />But hey, even legends need to focus. Maybe narrow it down just a smidge? 🎯<br /><br />We believe in you. 💪",
          },
          {
            courseTitle: "Too many dreams, not enough hours 🕰️",
            sectionNote: "Are you building your own university? Try picking just a few classes for now. 🎓",
          },
          {
            courseTitle: "Filter Frenzy Detected ⚙️",
            sectionNote: "You've summoned the rare No Results screen! 🧙‍♂️ Maybe ease off the sliders?",
          },
          {
            courseTitle: "Schedule Overload 🚨",
            sectionNote: "This isn’t a buffet—you don’t have to sample everything at once. 🍽️",
          },
          {
            courseTitle: "Chill, Champ 🧊",
            sectionNote: "We admire your drive. Now take a deep breath and try filtering again. 🧘",
          }
        ];
        const randomIndex = Math.floor(Math.random() * easterEggMessages.length);
        setEasterEggMessage(easterEggMessages[randomIndex]);
        setEasterEggActive(true);
      }

      if (easterEggMessage) {
        setFilteredData([
          {
            crn: "00000",
            courseTitle: easterEggMessage.courseTitle,
            sectionNote: easterEggMessage.sectionNote,
            subjCode: "EAST",
            subjDesc: "Easter Eggs",
            courseNumbDisplay: "001",
            creditHrLow: "0",
            pfaName: "The System",
            campDesc: "All Campuses",
            ptrmDesc: "All Terms",
            schdDesc: "All Schedules",
            fwSectOpenInd: "C",
          }
        ]);
      }

      return;
    } else if (easterEggActive) {
      setEasterEggActive(false);
      setEasterEggMessage(null);
    }

    setFilteredData(filtered);
  }, [sectionData, selectedSubjectCode, selectedLocation, selectedPtrm, selectedInstructor, selectedAttribute, selectedScheduleCode, selectedDaysOfWeek, selectedStartTime, selectedEndTime, debouncedSearch, filterZTCOnly, filterOpenClasses, filterWaitlistClasses, filterCredit, filterNonCredit, courseNumberSearch, shouldShowFunCourses]);


  // Check if the app is in maintenance mode
  const isMaintenanceMode = false;

  // Konami Code key listener useEffect
  useEffect(() => {
    const sequence = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let index = 0;

    const keyHandler = (e) => {
      if (e.key === sequence[index]) {
        index++;
        if (index === sequence.length) {
          setMatrixMode(true);
          // --- Begin Matrix Rain Animation ---
          const canvas = document.getElementById('matrixCanvas');
          if (canvas) {
            const ctx = canvas.getContext('2d');
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            const letters = 'アァイィウエエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('');
            const fontSize = 14;
            const columns = canvas.width / fontSize;
            const drops = Array.from({ length: columns }).fill(1);

            const draw = () => {
              ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.fillStyle = '#0F0';
              ctx.font = `${fontSize}px monospace`;
              drops.forEach((y, i) => {
                const text = letters[Math.floor(Math.random() * letters.length)];
                const x = i * fontSize;
                ctx.fillText(text, x, y * fontSize);
                drops[i] = y * fontSize > canvas.height || Math.random() > 0.975 ? 0 : y + 1;
              });
            };

            matrixIntervalRef.current = setInterval(draw, 33);
            // Clean up interval on matrix mode deactivation or component unmount
            return () => clearInterval(matrixIntervalRef.current);
          }
          // --- End Matrix Rain Animation ---
          index = 0;
        }
      } else {
        index = 0;
      }
    };

    window.addEventListener('keydown', keyHandler);
    return () => window.removeEventListener('keydown', keyHandler);
  }, []);

  // fire once after 5 min
  useEffect(() => {
    const timer = setTimeout(() => {
      setUfoTop(`${Math.floor(Math.random() * 80)}vh`);               // random vertical start (0–80 vh)
      setUfoDirection(Math.random() < 0.5 ? 'ltr' : 'rtl');           // random fly direction
      setUfoActive(true);
    }, 30 * 60 * 1000);
    return () => clearTimeout(timer);
  }, []);

  // If in maintenance mode, render the MaintenancePage component
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <div className={`flex flex-col min-h-screen bg-pcc-lighter-gray ${matrixMode ? 'matrix-mode' : ''}`}>
      {/* UFO fly-over overlay */}
      {ufoActive && (
        <div
          className={`fixed ${ufoDirection === 'ltr'
            ? 'left-0 animate-ufo-fly'
            : 'right-0 animate-ufo-fly-rtl'
            } z-[60] pointer-events-none`}
          style={{
            top: ufoTop,
            '--ufo-duration': `${(Math.random() * 0.1 + 0.1).toFixed(2)}s`
          }}
          onAnimationEnd={() => setUfoActive(false)}
        >
          <span className="text-6xl">🛸</span>
        </div>
      )}

      {/* Matrix Rain Overlay */}
      <div className="matrix-rain fixed inset-0 z-50 pointer-events-none">
        <canvas id="matrixCanvas" className="w-full h-full"></canvas>
      </div>
      {matrixMode && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => {
              setMatrixMode(false);
              const canvas = document.getElementById('matrixCanvas');
              if (canvas) {
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
              }
              if (matrixIntervalRef.current) {
                clearInterval(matrixIntervalRef.current);
              }
            }}
            className="bg-black text-green-400 border border-green-400 px-4 py-2 rounded shadow-md hover:bg-green-800 transition"
          >
            Deactivate Matrix Mode
          </button>
        </div>
      )}
      {/* End Rain Overlay */}


      <Header className="bg-pcc-cardinal text-white p-4" />
      {/* <Carousel /> */}
      <Alert />

      <Summary />

      {/* --- TOP FILTERS SECTION --- */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mt-6 px-4 w-full max-w-screen-lg mx-auto">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 whitespace-nowrap">Class Schedule for</span>
          <div className="min-w-[220px]">
            <Dropdown
              title="Term"
              fetchUrl={`${apiUrl}/getClassScheduleTerms`}
              resultKey="termDisplayControl"
              valueField="termCode"
              labelField="termName"

              onFilterSelect={setSelectedTerm}
              isMulti={false}
            />
          </div>
        </div>
        <div className="h-10 w-px bg-gray-300 hidden sm:block" />
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-sm text-gray-600">Search</span>
          <div className="w-full sm:w-[400px] md:w-[500px] lg:w-[600px]">
            <SearchBox onSearch={setSearchBoxText} />
          </div>
        </div>
      </div>
      {/* --- END TOP FILTERS SECTION --- */}


      {selectedTerm && (
        <>
          <div className="px-4 w-full max-w-screen-lg mx-auto mt-6 space-y-6">
            {/* Course Info */}
            <div>
              <h3 className="text-md font-semibold text-pcc-dark-gray mb-2">Course Info</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Subject (formerly Subject Code), Course Number, Instructor, Term Duration */}
                <div>
                  <div className="font-semibold text-sm mb-1">Subject</div>
                  <Dropdown title="Subject" options={subjectCodeOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedSubjectCode} isMulti={true} />
                </div>
                {/* Course Number Search Input */}
                <div>
                  <div className="font-semibold text-sm mb-1">Course Number</div>
                  <input
                    type="text"
                    placeholder="e.g. 001B"
                    value={courseNumberSearch}
                    onChange={(e) => setCourseNumberSearch(e.target.value)}
                    className="w-full h-11 border rounded-md px-3 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-pcc-gold"
                  />
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Instructor</div>
                  <Dropdown title="Instructor" options={instructorOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedInstructor} isMulti={true} />
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Term Duration</div>
                  <Dropdown title="Term" options={ptrmOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedPtrm} isMulti={true} />
                </div>
              </div>
            </div>

            {/* Logistics */}
            <div>
              <h3 className="text-md font-semibold text-pcc-dark-gray mb-2">Logistics</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Location/Campus */}
                <div>
                  <div className="font-semibold text-sm mb-1">Location/Campus</div>
                  <Dropdown title="Location" options={locationOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedLocation} isMulti={true} />
                </div>
                {/* Schedule Type */}
                <div>
                  <div className="font-semibold text-sm mb-1">Schedule Type</div>
                  <Dropdown title="Schedule Type" options={scheduleCodeOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedScheduleCode} isMulti={true} />
                </div>
                {/* Day of Week */}
                <div>
                  <div className="font-semibold text-sm mb-1">Day of Week</div>
                  <Dropdown title="Days" options={[{ label: 'Mon', value: 'M' }, { label: 'Tue', value: 'T' }, { label: 'Wed', value: 'W' }, { label: 'Thu', value: 'R' }, { label: 'Fri', value: 'F' }, { label: 'Sat', value: 'S' }, { label: 'Sun', value: 'U' }]} onFilterSelect={setSelectedDaysOfWeek} isMulti={true} />
                </div>
                {/* Start Time */}
                <div>
                  <div className="font-semibold text-sm mb-1">Start Time</div>
                  <Dropdown title="Start Time" options={[{ label: '8:00 AM', value: '08:00' }, { label: '9:00 AM', value: '09:00' }, { label: '10:00 AM', value: '10:00' }, { label: '11:00 AM', value: '11:00' }, { label: '12:00 PM', value: '12:00' }, { label: '1:00 PM', value: '13:00' }, { label: '2:00 PM', value: '14:00' }, { label: '3:00 PM', value: '15:00' }, { label: '4:00 PM', value: '16:00' }, { label: '5:00 PM', value: '17:00' }, { label: '6:00 PM', value: '18:00' }]} onFilterSelect={val => setSelectedStartTime(Array.isArray(val) ? (val[0] || '') : (val || ''))} isMulti={false} />
                </div>
                {/* End Time */}
                <div>
                  <div className="font-semibold text-sm mb-1">End Time</div>
                  <Dropdown title="End Time" options={[{ label: '9:00 AM', value: '09:00' }, { label: '10:00 AM', value: '10:00' }, { label: '11:00 AM', value: '11:00' }, { label: '12:00 PM', value: '12:00' }, { label: '1:00 PM', value: '13:00' }, { label: '2:00 PM', value: '14:00' }, { label: '3:00 PM', value: '15:00' }, { label: '4:00 PM', value: '16:00' }, { label: '5:00 PM', value: '17:00' }, { label: '6:00 PM', value: '18:00' }, { label: '7:00 PM', value: '19:00' }]} onFilterSelect={val => setSelectedEndTime(Array.isArray(val) ? (val[0] || '') : (val || ''))} isMulti={false} />
                </div>

              </div>
            </div>

            {/* Attributes */}
            <div>
              <h3 className="text-md font-semibold text-pcc-dark-gray mb-2">Attributes</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_2fr] gap-4">
                <div className="flex flex-col justify-between h-full">
                  <div>
                    <div className="font-semibold text-sm mb-1">Attribute</div>
                    <Dropdown
                      title="Attribute"
                      fetchUrl={`${apiUrl}/courseCategories`}
                      resultKey="courseCategories11"
                      valueField="code"
                      labelField="title"
                      onFilterSelect={setSelectedAttribute}
                      isMulti={true}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {/* Zero Textbook Costs */}
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <div className="font-semibold text-sm mb-1">Zero Textbook Costs</div>
                      <div className="flex items-center mt-1">
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="ztcFilter"
                            checked={filterZTCOnly}
                            onChange={(e) => setFilterZTCOnly(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          ZTC Only Classes
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Open Class */}
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <div className="font-semibold text-sm mb-1">Status</div>
                      <div className="flex flex-col mt-1 space-y-1">
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="openClasses"
                            checked={filterOpenClasses}
                            onChange={(e) => setFilterOpenClasses(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Open
                        </label>
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="waitlistClasses"
                            checked={filterWaitlistClasses}
                            onChange={(e) => setFilterWaitlistClasses(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Waitlist
                        </label>
                      </div>
                    </div>
                  </div>


                  {/* Class Type */}
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <div className="font-semibold text-sm mb-1">Class Type</div>
                      <div className="flex flex-col mt-1 space-y-1">
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="creditFilter"
                            checked={filterCredit}
                            onChange={(e) => setFilterCredit(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Credit
                        </label>
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="nonCreditFilter"
                            checked={filterNonCredit}
                            onChange={(e) => setFilterNonCredit(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Noncredit
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      <div className="flex mt-6">
        <SectionCards data={filteredData} loading={loading} selectedTerm={selectedTerm} />
      </div>
      <Footer className="bg-pcc-cardinal text-white p-4 mt-auto" />
    </div>
  );
}

export default App;